import { computed, unref, type Ref } from "vue";

type MaybeRef<T> = T | Ref<T>;

type SubjectLike = {
  id?: string;
  _id?: string;
  code?: string;
  name?: string;
};

type ClassGroupLike = {
  _id: string;
  name: string;
  subject: string;
  gradeLevel: number;
};

const DEFAULT_SUBJECT_ORDER = ["ENGLISH", "MATHS"];

export function useGradeSubjectFilterOptions(
  classGroupsSource: MaybeRef<ClassGroupLike[] | null | undefined>,
  subjectsSource: MaybeRef<SubjectLike[] | null | undefined>,
  subjectOrder: string[] = DEFAULT_SUBJECT_ORDER
) {
  const gradeSubjectOptions = computed(() => {
    const classGroups = (unref(classGroupsSource) || []) as ClassGroupLike[];
    const subjects = (unref(subjectsSource) || []) as SubjectLike[];

    const subjectsById = new Map<string, { code: string; name: string }>();
    subjects.forEach((subject) => {
      const id = String(subject._id ?? subject.id ?? "");
      if (!id) return;
      const code = subject.code || subject.name || id;
      const name = subject.name || subject.code || id;
      subjectsById.set(id, { code, name });
    });

    const gradesMap = new Map<
      number,
      Map<string, { name: string; items: Array<{ value: string; label: string; sortKey: string }> }>
    >();

    classGroups.forEach((cls) => {
      if (!cls || cls.gradeLevel === null || cls.gradeLevel === undefined) return;

      const gradeLevel = Number(cls.gradeLevel);
      const subjectMeta = subjectsById.get(String(cls.subject));
      const subjectCodeRaw = subjectMeta?.code || subjectMeta?.name || "UNKNOWN";
      const subjectCode = subjectCodeRaw.toUpperCase();
      const subjectName = subjectMeta?.name || subjectMeta?.code || subjectCodeRaw;

      if (!gradesMap.has(gradeLevel)) {
        gradesMap.set(gradeLevel, new Map());
      }

      const subjectMap = gradesMap.get(gradeLevel) as Map<
        string,
        { name: string; items: Array<{ value: string; label: string; sortKey: string }> }
      >;

      if (!subjectMap.has(subjectCode)) {
        subjectMap.set(subjectCode, { name: subjectName, items: [] });
      }

      const label = `${cls.name} (${subjectName}, Grade ${gradeLevel})`;
      subjectMap.get(subjectCode)?.items.push({
        value: cls._id,
        label,
        sortKey: label,
      });
    });

    const sortedGrades = Array.from(gradesMap.keys()).sort((a, b) => a - b);
    const normalizedOrder = subjectOrder.map((code) => code.toUpperCase());

    const options: Array<{
      groupLabel: string;
      groupValue: string;
      items: Array<{ label: string; value: string }>;
    }> = [];

    sortedGrades.forEach((grade) => {
      const subjectMap = gradesMap.get(grade);
      if (!subjectMap) return;

      const subjectCodes = Array.from(subjectMap.keys()).sort((a, b) => {
        const aIndex = normalizedOrder.indexOf(a.toUpperCase());
        const bIndex = normalizedOrder.indexOf(b.toUpperCase());
        if (aIndex !== -1 || bIndex !== -1) {
          return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
        }
        return a.localeCompare(b);
      });

      subjectCodes.forEach((subjectCode) => {
        const subjectGroup = subjectMap.get(subjectCode);
        if (!subjectGroup) return;

        const items = [...subjectGroup.items].sort((a, b) =>
          a.sortKey.localeCompare(b.sortKey)
        );

        options.push({
          groupLabel: `Grade ${grade} (${subjectGroup.name})`,
          groupValue: `grade-${grade}-${subjectCode}`,
          items: items.map((item) => ({
            label: item.label,
            value: item.value,
          })),
        });
      });
    });

    return options;
  });

  return {
    gradeSubjectOptions,
  };
}
